import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { getApiBaseUrl } from '../../services/api';
import { SessionDeck } from '../../components/companion/SessionDeck';
import { AppSourceContext } from '../../components/companion/AppLogoWall';
import type { AppSource } from '../../components/companion/AppLogoWall';
import type { CatalogGroup } from '../../services/setupApi';
import type { AnswerApi } from '../../components/companion/SessionDeck';
import type { CompanionSessionCard } from '../../services/companionApi';
import type { SessionApp, SessionFileAnswer } from '../../types/aai';
import { ErrorNote, Loading } from '../../components/aa';
import { useAsync } from '../aai/useAsync';

interface LinkSession {
  company: string;
  email: string;
  title: string;
  answered: boolean;
  session: (CompanionSessionCard & { title: string }) | null;
}

const base = (token: string) => `${getApiBaseUrl()}/aai/public/session-answer/${encodeURIComponent(token)}`;

const tokenApi = (token: string): AnswerApi => ({
  upload: (_sessionId, questionId, file) => {
    const form = new FormData();
    form.append('file', file);
    return axios.post<{ files: SessionFileAnswer[] }>(`${base(token)}/questions/${encodeURIComponent(questionId)}/files`, form);
  },
  remove: (_sessionId, questionId, fileId) =>
    axios.delete<{ files: SessionFileAnswer[] }>(`${base(token)}/questions/${encodeURIComponent(questionId)}/files/${fileId}`),
  submit: (_sessionId, answers) => axios.post(`${base(token)}/submit`, { answers }),
});

const tokenApps = (token: string): AppSource => {
  let request: Promise<CatalogGroup[]> | null = null;
  const catalog = (): Promise<CatalogGroup[]> => {
    if (!request) {
      request = axios.get<{ groups: CatalogGroup[] }>(`${base(token)}/apps`).then((r) => r.data.groups).catch(() => {
        request = null;
        return [];
      });
    }
    return request;
  };
  const search = async (query: string): Promise<SessionApp[]> => {
    const needle = query.trim().toLowerCase();
    const groups = await catalog();
    const out: SessionApp[] = [];
    groups.forEach((g) => g.apps.forEach((a) => {
      if (a.label.toLowerCase().includes(needle) && !out.some((o) => o.slug === a.slug)) {
        out.push({ slug: a.slug, label: a.label, logo: a.logo });
      }
    }));
    return out;
  };
  return { catalog, search };
};

const Done: React.FC<{ company: string; title: string }> = ({ company, title }) => (
  <div className="px-6 py-12 text-center">
    <CheckCircle2 size={36} className="mx-auto text-ok" aria-hidden />
    <h1 className="m-0 mt-4 text-[22px] font-bold leading-[29px] text-ink">Thank you, your answers are in</h1>
    <p className="mx-auto mt-2 max-w-[44ch] text-[14px] leading-[22px] text-ink-muted">
      {`Your answers to "${title}" went to ${company || 'the team'}. You can close this page.`}
    </p>
  </div>
);

const SessionAnswerPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [sent, setSent] = useState(false);
  const answerApi = useMemo(() => tokenApi(token), [token]);
  const apps = useMemo(() => tokenApps(token), [token]);
  const loaded = useAsync<LinkSession>(() => axios.get<LinkSession>(base(token)), 'This link is not valid any more.', [token]);

  let body: React.ReactNode = <div className="p-10"><Loading label="Opening your questions" /></div>;
  const failure = loaded.error !== null && !loaded.loading;
  if (failure) {
    body = (
      <div className="p-8">
        <ErrorNote message="This link is not valid any more. Ask the person who sent it for a new one." />
      </div>
    );
  }
  const data = loaded.data;
  if (data && (data.answered || sent)) body = <Done company={data.company} title={data.title} />;
  if (data && !data.answered && !sent && !data.session) {
    body = <div className="p-8"><ErrorNote message="These questions are no longer open." /></div>;
  }
  if (data && !data.answered && !sent && data.session) {
    body = (
      <div className="flex h-[min(680px,calc(100vh-180px))] min-h-[460px] flex-col">
        <AppSourceContext.Provider value={apps}>
          <SessionDeck
            title={data.session.title}
            session={data.session}
            onBack={() => undefined}
            onSent={() => setSent(true)}
            api={answerApi}
            startLabel=""
          />
        </AppSourceContext.Provider>
      </div>
    );
  }

  return (
    <main className="mx-auto w-full max-w-[620px] px-4 py-8 sm:py-12">
      {data && !sent && !data.answered && (
        <div className="mb-5">
          <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft">{data.company}</p>
          <p className="m-0 mt-1.5 text-[14px] leading-[22px] text-ink-muted">
            A few questions for {data.email}. Answer one at a time; your answers are sent when you finish.
          </p>
        </div>
      )}
      <div className="overflow-hidden rounded-panel border border-line bg-white shadow-[0_1px_2px_rgba(24,25,29,0.06),0_8px_24px_rgba(24,25,29,0.06)]">
        {body}
      </div>
    </main>
  );
};

export default SessionAnswerPage;
